import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(
    () => localStorage.getItem('impersonating') === '1',
  );

  const refreshCredits = useCallback(async () => {
    try {
      const r = await api.get('/me/credits');
      setCredits(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setCredits(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
      localStorage.setItem('user', JSON.stringify(r.data));
      await refreshCredits();
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setUser(null);
      setCredits(null);
    } finally {
      setLoading(false);
    }
  }, [refreshCredits]);

  useEffect(() => {
    refresh();
    const onLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setUser(null);
      setCredits(null);
    };
    window.addEventListener('photoplan:logout', onLogout);
    return () => window.removeEventListener('photoplan:logout', onLogout);
  }, [refresh]);

  const login = async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', r.data.access_token);
    if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
    localStorage.setItem('user', JSON.stringify(r.data.user));
    setUser(r.data.user);
    await refreshCredits();
    return r.data.user;
  };

  const register = async ({ name, email, password, access_code }) => {
    const r = await api.post('/auth/register', { name, email, password, access_code });
    localStorage.setItem('token', r.data.access_token);
    if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
    localStorage.setItem('user', JSON.stringify(r.data.user));
    setUser(r.data.user);
    await refreshCredits();
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('imp_admin_token');
    localStorage.removeItem('imp_admin_refresh_token');
    localStorage.removeItem('imp_admin_user');
    setImpersonating(false);
    setUser(null);
    setCredits(null);
  };

  // Admin: view the app as another user. Stash the admin session, swap in the
  // target user's freshly minted token, become them. stopImpersonating restores.
  const impersonate = async (userId) => {
    const r = await api.post(`/admin/users/${userId}/impersonate`);
    localStorage.setItem('imp_admin_token', localStorage.getItem('token') || '');
    localStorage.setItem('imp_admin_refresh_token', localStorage.getItem('refresh_token') || '');
    localStorage.setItem('imp_admin_user', localStorage.getItem('user') || '');
    localStorage.setItem('token', r.data.access_token);
    if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
    localStorage.setItem('user', JSON.stringify(r.data.user));
    localStorage.setItem('impersonating', '1');
    setImpersonating(true);
    setUser(r.data.user);
    await refreshCredits();
    return r.data.user;
  };

  const stopImpersonating = async () => {
    const adminToken = localStorage.getItem('imp_admin_token');
    const adminRefresh = localStorage.getItem('imp_admin_refresh_token');
    const adminUser = localStorage.getItem('imp_admin_user');
    if (adminToken) localStorage.setItem('token', adminToken);
    if (adminRefresh) localStorage.setItem('refresh_token', adminRefresh);
    if (adminUser) localStorage.setItem('user', adminUser);
    localStorage.removeItem('impersonating');
    localStorage.removeItem('imp_admin_token');
    localStorage.removeItem('imp_admin_refresh_token');
    localStorage.removeItem('imp_admin_user');
    setImpersonating(false);
    await refresh();
  };

  const isTrialExpired = !!(
    user?.plan_id === 'free_trial' &&
    user?.trial_end_date &&
    new Date(user.trial_end_date) < new Date()
  );
  const isFrozen = user?.status === 'frozen';
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider
      value={{
        user, credits, loading,
        login, register, logout, refresh, refreshCredits,
        isTrialExpired, isFrozen, isAdmin,
        impersonating, impersonate, stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
